/* WeQuote Dev Board — migrate(): the authoritative card-defaulting discipline
 * (BUILD-PRD §4, verify §2/§18). Every persisted field gets a safe default so
 * old/imported records upgrade cleanly. Ported line-for-line from app.js. */
import type { Feature } from './types'
import { normaliseStage } from './stages'

export function firstReqDate(f: Partial<Feature>): string {
  let d = ''
  ;(f.requests || []).forEach((r) => { if (r.date && (!d || r.date < d)) d = r.date })
  return d
}

export function migrate(f: any): Feature {
  if (f.status === 'backlog') f.status = 'later' // v2 §1: Backlog band becomes Later
  if (f.start === undefined) f.start = ''
  if (f.progress === undefined) f.progress = 0
  if (f.pnotes === undefined) f.pnotes = ''
  if (f.onhold === undefined) f.onhold = false
  if (f.prevStatus === undefined) f.prevStatus = ''
  if (f.stash === undefined) f.stash = null
  if (f.designDeadline === undefined) f.designDeadline = ''
  if (f.designProgress === undefined) f.designProgress = 0 // designer-set % toward finishing the design
  if (f.designStart === undefined) f.designStart = '' // date design work began (auto-set on entering Design)
  if (f.designNotes === undefined) f.designNotes = '' // design progress notes (mirrors dev pnotes)
  if (f.designCompletedOn === undefined) f.designCompletedOn = '' // stamped when it reaches Design Complete
  if (f.announcedOn === undefined) f.announcedOn = '' // date requesters were told it shipped
  if (f.publishedOn === undefined) f.publishedOn = '' // date it went on the customer "updates" page
  if (f.noAnnounce === undefined) f.noAnnounce = false // "no announcement needed" (bug fix / custom work)
  if (f.whatsappOn === undefined) f.whatsappOn = '' // announced in the WhatsApp support groups
  if (f.triagePinned === undefined) f.triagePinned = false // locked position in the suggested triage view
  if (f.triagePos === undefined) f.triagePos = null // locked slot index when pinned
  if (f.blocker === undefined) f.blocker = false // marked a blocker → floats to top of triage
  if (!Array.isArray(f.checklist)) f.checklist = [] // [{text, done}] — all must be ticked before Live
  if (f.estCompletion === undefined) f.estCompletion = '' // projected finish (auto from roadmap schedule)
  if (f.pinnedStart === undefined) f.pinnedStart = false // start manually anchored on the roadmap
  if (f.riskAcknowledged === undefined) f.riskAcknowledged = false // "happy with it running late"
  // Actual completion date — only meaningful once a card is Live
  if (f.completedOn === undefined) f.completedOn = f.status === 'live' ? f.deadline || '' : ''
  if (!Array.isArray(f.links)) f.links = []
  if (!Array.isArray(f.comments)) {
    // Seed the thread from any existing single note so nothing is lost
    f.comments = f.notes && ('' + f.notes).trim() ? [{ author: 'Imported note', text: f.notes, ts: null }] : []
  }
  if (f.requiresDesign === undefined) f.requiresDesign = true // existing cards assumed to need design
  if (!Array.isArray(f.requests)) f.requests = [] // {company, person, date}
  // Date added to the board — backfill old cards from their earliest customer request
  if (f.dateAdded === undefined) f.dateAdded = firstReqDate(f)
  if (f.source === undefined) f.source = null // {type:'manual'|'chat', by, company, person, ts}
  if (f.archiveReason === undefined) f.archiveReason = ''
  if (f.customerDesc === undefined) f.customerDesc = '' // short customer-facing blurb for the roadmap export
  if (f.customerName === undefined) f.customerName = '' // customer-facing feature name (blank = use the title)
  if (f.showOnRoadmap === undefined) f.showOnRoadmap = true // include on the customer roadmap export
  if (f.isBug === undefined) f.isBug = false // flags this card as a bug (filterable)
  if (f.notes === undefined) f.notes = ''
  if (f.module === undefined) f.module = '' // Module (Lee, 28 Aug) — '' only for imported cards
  if (f.cust === undefined) f.cust = ''
  // Intake alert recipient — existing review cards are treated as already alerted
  if (f.alertEmailedTo === undefined) f.alertEmailedTo = f.status === 'needsreview' ? 'nl@wetech.cloud' : ''
  if (!Array.isArray(f.notifications)) f.notifications = [] // emails the server actually sent for this card
  // Staged delivery (v2 §2): a big job broken into scheduled stages
  if (f.multiStage === undefined) f.multiStage = false
  if (!Array.isArray(f.stages)) f.stages = []
  if (f.timeStash === undefined) f.timeStash = ''
  f.stages.forEach(normaliseStage)
  return f as Feature
}
